import { API_METHODS } from '@compass/client/constants/api-methods';
import { Check, Edit2, KeyRound, LogOut, X } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AvatarWithStatus } from '@/components/AvatarWithStatus';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { RoleBadge } from '@/components/RoleBadge';
import { DeveloperModeSection } from '@/components/settings/DeveloperModeSection';
import { MfaSettingsSection } from '@/components/settings/MfaSettingsSection';
import { OrganizationMembers } from '@/components/settings/OrganizationMembers';
import { OrgKeysSection } from '@/components/settings/OrgKeysSection';
import { ProgramMembers } from '@/components/settings/ProgramMembers';
import { ServiceAccountsSection } from '@/components/settings/ServiceAccountsSection';
import { UserSkillBadge } from '@/components/settings/UserSkillBadge';
import { WebhooksSection } from '@/components/settings/WebhooksSection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UpdatePasswordDialog } from '@/components/UpdatePasswordDialog';
import { useLogout } from '@/hooks/useAuth';
import { useIs100msUser } from '@/hooks/useIs100msUser';
import { useIsSystemAdmin } from '@/hooks/useIsSystemAdmin';
import {
  useAPIPermission,
  useCurrentUserContextRoles,
  usePermissionMap,
} from '@/hooks/usePermissions';
import { useShouldShowDeveloperSettings } from '@/hooks/useShouldShowDeveloperSettings';
import { useCurrentUser, useUpdateCurrentUser } from '@/hooks/useUsers';
import { useCurrentUserSkills } from '@/hooks/useUserSkills';
import { useStore } from '@/store';

import { parseSettingsTab, type SettingsTab } from './settingsTabs';

type FieldName = 'name' | 'title';

export const SettingsPage: React.FC = () => {
  const [editingField, setEditingField] = useState<FieldName | null>(null);
  const [formData, setFormData] = useState({ name: '', title: '' });
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedOrganizationId, selectedProgramId, testModeEnabled } = useStore();
  const { data: userData, isLoading: userLoading } = useCurrentUser(
    selectedOrganizationId ?? undefined,
    selectedProgramId ?? undefined,
  );
  const updateMutation = useUpdateCurrentUser();
  const logout = useLogout();
  const is100msUser = useIs100msUser();
  const isSystemAdmin = useIsSystemAdmin();
  const { roles, isLoading: rolesLoading } = useCurrentUserContextRoles();
  const { data: skillsData, isLoading: skillsLoading } = useCurrentUserSkills();

  const user = userData?.user;

  // Skills are scoped per-organization; show only those for the selected org.
  const orgSkills = (skillsData?.skills || []).filter(
    skill => skill.organization_id === selectedOrganizationId,
  );

  // Check permissions for Program and Organization tabs
  const { hasPermission: canAccessProgram } = useAPIPermission([
    API_METHODS.ProgramUserService.SearchProgramUsers,
    API_METHODS.ProgramRoleService.ListProgramRoles,
  ]);

  const { hasPermission: canAccessOrganization } = useAPIPermission([
    API_METHODS.OrganizationUserService.SearchOrganizationUsers,
    API_METHODS.OrganizationRoleService.ListOrganizationRoles,
    API_METHODS.OrganizationsService.AddUserToOrganization,
  ]);

  // Webhooks, API keys, service accounts and developer mode: Organization
  // Admins (and system admins) only, as there is no developer permission.
  const { shouldShow: canAccessDeveloper, isLoading: developerLoading } =
    useShouldShowDeveloperSettings();

  // The tab lives in `?tab=` so other pages can link to it (Webhooks links
  // back to Developer). A tab the viewer cannot open falls back to Account
  // once their permissions are known; until then the requested tab is held,
  // so a deep link does not flash Account first.
  const { isLoading: permissionsLoading } = usePermissionMap();
  const requestedTab = parseSettingsTab(searchParams.get('tab'));
  const tabAccess: Record<SettingsTab, { allowed: boolean; loading: boolean }> = {
    account: { allowed: true, loading: false },
    program: { allowed: canAccessProgram, loading: permissionsLoading },
    organization: { allowed: canAccessOrganization, loading: permissionsLoading },
    developer: { allowed: canAccessDeveloper, loading: developerLoading },
  };
  const { allowed: requestedAllowed, loading: requestedLoading } = tabAccess[requestedTab];
  const activeTab: SettingsTab = requestedAllowed || requestedLoading ? requestedTab : 'account';

  const handleTabChange = (value: string) => {
    const tab = parseSettingsTab(value);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (tab === 'account') next.delete('tab');
        else next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  };

  const handleEditField = (field: FieldName) => {
    setFormData({
      name: user?.name || '',
      title: user?.title || '',
    });
    setEditingField(field);

    // Focus the input after state update
    setTimeout(() => {
      if (field === 'name') {
        nameInputRef.current?.focus();
      } else if (field === 'title') {
        titleInputRef.current?.focus();
      }
    }, 0);
  };

  const handleCancelField = () => {
    setEditingField(null);
    setFormData({ name: '', title: '' });
  };

  const handleSaveField = async (field: FieldName) => {
    const value = formData[field].trim();
    if (value && value !== user?.[field]) {
      await updateMutation.mutateAsync({ [field]: value });
    }
    setEditingField(null);
  };

  const handleFieldChange = (field: FieldName, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: FieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveField(field);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelField();
    }
  };
  const isLoading = userLoading && !user;

  return (
    <>
      <BreadcrumbPortal>
        <Breadcrumb>
          <Breadcrumb.Item>Settings</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbPortal>
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto bg-background">
          <div className="max-w-4xl mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold">Settings</h1>
                <p className="text-sm text-muted-foreground">Manage your account</p>
              </div>
              <Button
                variant="outline"
                onClick={logout}
                className="shrink-0 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList>
                <TabsTrigger value="account">Account</TabsTrigger>
                {!isLoading && canAccessProgram && (
                  <TabsTrigger value="program">Program</TabsTrigger>
                )}
                {!isLoading && canAccessOrganization && (
                  <TabsTrigger value="organization">Organization</TabsTrigger>
                )}
                {!isLoading && canAccessDeveloper && (
                  <TabsTrigger value="developer">Developer</TabsTrigger>
                )}
              </TabsList>

              {/* Account Tab */}
              <TabsContent value="account">
                <div className="rounded-lg border bg-card p-6">
                  <div className="mb-4">
                    <h2 className="text-lg font-semibold">Profile Information</h2>
                    <p className="text-sm text-muted-foreground">Update your profile information</p>
                  </div>

                  <div className="flex gap-8">
                    {/* Left side - Avatar, Email, Role */}
                    <div className="flex flex-col items-center self-center">
                      {isLoading ? (
                        <>
                          <Skeleton className="h-12 w-12 rounded-full" />
                          <div className="mt-3 flex flex-col items-center">
                            <Skeleton className="h-4 w-48 mb-2" />
                            <Skeleton className="h-5 w-24" />
                          </div>
                        </>
                      ) : (
                        <>
                          <AvatarWithStatus
                            name={user?.name}
                            email={user?.email}
                            userId={user?.id}
                            size="xl"
                          />
                          <div className="flex flex-col mt-3 text-center gap-2">
                            <p className="text-sm text-muted-foreground">{user?.email}</p>
                            {rolesLoading ? (
                              <Skeleton className="h-5 w-32" />
                            ) : (
                              roles.length > 0 && (
                                <div className="flex flex-col gap-1 items-center">
                                  {roles.map(role => (
                                    <RoleBadge key={role.id} role={role} size="sm" />
                                  ))}
                                </div>
                              )
                            )}
                            {!skillsLoading && orgSkills.length > 0 && (
                              <div className="flex max-w-[220px] flex-wrap justify-center gap-1">
                                {orgSkills.map(skill => (
                                  <UserSkillBadge key={skill.skill} skill={skill} />
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Separator */}
                    <Separator orientation="vertical" className="h-auto" />

                    {/* Right side - Form fields */}
                    <div className="flex-1 space-y-6">
                      {/* Name and Title Fields */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Name Field */}
                        <div>
                          <Label htmlFor="name" className="mb-3 block text-sm font-medium">
                            Name
                          </Label>
                          {isLoading ? (
                            <Skeleton className="h-10 w-full" />
                          ) : (
                            <div className="flex items-center gap-2">
                              <Input
                                ref={nameInputRef}
                                id="name"
                                value={editingField === 'name' ? formData.name : user?.name || ''}
                                onChange={e => handleFieldChange('name', e.target.value)}
                                onKeyDown={e => handleKeyDown(e, 'name')}
                                readOnly={editingField !== 'name'}
                                disabled={updateMutation.isPending}
                                className={`h-10 ${editingField !== 'name' ? 'bg-muted text-foreground pointer-events-none' : ''}`}
                                placeholder="Enter your name"
                              />
                              {editingField === 'name' ? (
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => handleSaveField('name')}
                                    disabled={updateMutation.isPending}
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={handleCancelField}
                                    disabled={updateMutation.isPending}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleEditField('name')}
                                  disabled={userLoading || editingField !== null}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Title Field */}
                        <div>
                          <Label htmlFor="title" className="mb-3 block text-sm font-medium">
                            Title
                          </Label>
                          {isLoading ? (
                            <Skeleton className="h-10 w-full" />
                          ) : (
                            <div className="flex items-center gap-2">
                              <Input
                                ref={titleInputRef}
                                id="title"
                                value={
                                  editingField === 'title' ? formData.title : user?.title || ''
                                }
                                onChange={e => handleFieldChange('title', e.target.value)}
                                onKeyDown={e => handleKeyDown(e, 'title')}
                                readOnly={editingField !== 'title'}
                                disabled={updateMutation.isPending}
                                className={`h-10 ${editingField !== 'title' ? 'bg-muted text-foreground pointer-events-none' : ''}`}
                                placeholder="Enter your title"
                              />
                              {editingField === 'title' ? (
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => handleSaveField('title')}
                                    disabled={updateMutation.isPending}
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={handleCancelField}
                                    disabled={updateMutation.isPending}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleEditField('title')}
                                  disabled={userLoading || editingField !== null}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Update Password */}
                      <div>
                        <Label className="mb-3 block text-sm font-medium">Password</Label>
                        <Button
                          variant="outline"
                          onClick={() => setIsPasswordDialogOpen(true)}
                          disabled={isLoading}
                          className="h-10"
                        >
                          <KeyRound className="h-4 w-4 mr-2" />
                          Update Password
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Two-Factor Authentication */}
                <MfaSettingsSection />

                {/* Developer mode lives in the Developer tab; someone without that tab
                    who still has the mode on keeps the switch here to turn it off. */}
                {!canAccessDeveloper && !developerLoading && testModeEnabled && (
                  <DeveloperModeSection />
                )}
              </TabsContent>

              {/* Program Tab */}
              <TabsContent value="program">{canAccessProgram && <ProgramMembers />}</TabsContent>

              {/* Organization Tab */}
              <TabsContent value="organization">
                {canAccessOrganization && <OrganizationMembers />}
              </TabsContent>

              {/* Developer Tab */}
              <TabsContent value="developer" className="[&>div:first-child]:mt-0">
                {canAccessDeveloper && (
                  <>
                    <WebhooksSection />
                    <OrgKeysSection />
                    {/* Service accounts stay 100ms system admins only (BI-908). */}
                    {is100msUser && isSystemAdmin && <ServiceAccountsSection />}
                    <DeveloperModeSection />
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Update Password Dialog */}
      <UpdatePasswordDialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen} />
    </>
  );
};
